/* frontend/src/pages/VehicleDetail.js */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil, Download, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api, { getImageUrl } from '../api';

// Shadcn UI components
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from "../lib/use-toast.js";
import { VEHICLE_STATUS_LABELS, VEHICLE_STATUS_CLASSES } from '../utils/vehicleStatuses';

/* getter seguro */
const G = (data, path) =>
  path.split('.').reduce((o, k) => (o ? o[k] : undefined), data) ?? '-';

/* monta URL absoluta para fotos/vídeos */
const url = getImageUrl;

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();

  // Função para deletar arquivos
  const handleDeleteFile = async (filePath, type, index) => {
    if (!window.confirm(t('vehicleDetail.confirmDeleteFile'))) return;
    
    try {
      await api.delete(`/veiculos/${id}/files`, {
        data: { filePath, type, index }
      });
      
      // Recarregar dados após deletar
      const response = await api.get(`/veiculos/${id}`);
      setData(response.data);
      
      alert(t('vehicleDetail.fileDeletedSuccess'));
    } catch (error) {
      console.error('Erro ao deletar arquivo:', error);
      alert(t('vehicleDetail.fileDeleteError') + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteVehicle = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!data) return;
    
    try {
      setDeleting(true);
      await api.delete(`/veiculos/${id}`);
      
      toast({
        title: t('vehicleDetail.vehicleDeleted'),
        description: t('vehicleDetail.vehicleDeletedDesc', { placa: data.placa })
      });
      
      // Redirecionar para a lista de veículos
      navigate('/admin/veiculos');
      
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      toast({
        variant: "destructive",
        title: t('vehicleDetail.deleteError'),
        description: t('vehicleDetail.deleteErrorDesc')
      });
    } finally {
      setDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  useEffect(() => { api.get(`/veiculos/${id}`).then(r => setData(r.data)); }, [id]);
  
  if (!data) return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" 
             aria-label={t('vehicleDetail.loading')}></div>
      </div>
    </div>
  );

  const infoFields = [
    { label: t('vehicleDetail.fields.placa'), path: 'placa' },
    { label: t('vehicleDetail.fields.placaMercosul'), path: 'placaMercosul' },
    { label: t('vehicleDetail.fields.marca'), path: 'infoTecnica.marca' },
    { label: t('vehicleDetail.fields.modelo'), path: 'infoTecnica.modelo' },
    { label: t('vehicleDetail.fields.versao'), path: 'infoTecnica.versao' },
    { label: t('vehicleDetail.fields.anoFabricacao'), path: 'infoTecnica.anoFabricacao' },
    { label: t('vehicleDetail.fields.anoModelo'), path: 'infoTecnica.anoModelo' },
    { label: t('vehicleDetail.fields.cor'), path: 'infoTecnica.cor' },
    { label: t('vehicleDetail.fields.combustivel'), path: 'infoTecnica.combustivel' },
    { label: t('vehicleDetail.fields.cambio'), path: 'infoTecnica.cambio' },
    { label: t('vehicleDetail.fields.km'), path: 'infoTecnica.km' }
  ];

  // Status rendering handled by centralized utility
  

  // Compatibilidade: alguns registros usam `midia` e `anexos`, outros `multimidia` e `documentos`
  const mediaItems = data.midia?.fotos || data.multimidia?.fotos || [];
  const attachmentItems = data.anexos || data.documentos || [];

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <Card className="overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              {data.infoTecnica?.marca} {data.infoTecnica?.modelo}
            </h1>
            <div className="flex gap-2">
              <Button
                variant="default"
                onClick={() => navigate(`/admin/veiculos/${id}/edit`)}
                className="flex items-center gap-1"
              >
                <Pencil className="h-4 w-4" />
                {t('vehicleDetail.edit')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteVehicle}
                className="flex items-center gap-1"
              >
                <Trash2 className="h-4 w-4" />
                {t('vehicleDetail.delete')}
              </Button>
            </div>
          </div>

          <Tabs 
            defaultValue="info" 
            value={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full"
          >
            <TabsList className="mb-2">
              <TabsTrigger value="info">{t('vehicleDetail.tabs.info')}</TabsTrigger>
              <TabsTrigger value="commercial">{t('vehicleDetail.tabs.commercial')}</TabsTrigger>
              <TabsTrigger value="media">{t('vehicleDetail.tabs.media')}</TabsTrigger>
              <TabsTrigger value="attachments">{t('vehicleDetail.tabs.attachments')}</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {infoFields.map((field) => (
                  <div 
                    key={field.path} 
                    className="p-4 rounded-lg bg-muted/50 border"
                  >
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      {field.label}
                    </p>
                    <p className="font-medium">
                      {field.render 
                        ? field.render(G(data, field.path)) 
                        : G(data, field.path)}
                    </p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="commercial" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-6 rounded-lg bg-muted/50 border">
                  <h3 className="text-sm font-medium text-muted-foreground mb-4">{t('vehicleDetail.origin.title')}</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{t('vehicleDetail.origin.lessor')}</p>
                      <p className="font-medium">{data.historico?.lessorId?.nomeFantasia || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('vehicleDetail.origin.patio')}</p>
                      <p className="font-medium">{data.historico?.patioNome || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('vehicleDetail.origin.status')}</p>
                      <span className={`px-2 py-1 text-xs rounded-[4px] inline-block ${VEHICLE_STATUS_CLASSES[G(data, 'historico.status')] || 'bg-gray-100 text-gray-800'}`}>
                        {VEHICLE_STATUS_LABELS[G(data, 'historico.status')] || G(data, 'historico.status') || '-'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 rounded-lg bg-muted/50 border">
                  <h3 className="text-sm font-medium text-muted-foreground mb-4">{t('vehicleDetail.values.title')}</h3>
                  <div className="space-y-4">
                    {[
                      [t('vehicleDetail.values.precoAquisicao'), 'comercial.precoAquisicao'],
                      [t('vehicleDetail.values.precoReparo'), 'comercial.precoReparo'],
                      [t('vehicleDetail.values.valorVenda'), 'comercial.valorVenda'],
                      [t('vehicleDetail.values.valorFipe'), 'comercial.valorFipe']
                    ].map(([label, path]) => (
                      <div key={path}>
                        <p className="text-sm text-muted-foreground">{label}</p>
                        <p className="font-medium text-primary">
                          {new Intl.NumberFormat('pt-BR', { 
                            style: 'currency', 
                            currency: 'BRL' 
                          }).format(G(data, path) || 0)}
                        </p>
                      </div>
                    ))}
                    <div>
                      <p className="text-sm text-muted-foreground">{t('vehicleDetail.values.gravame')}</p>
                      <p className="font-medium">{G(data, 'comercial.gravame') === 'true' ? t('common.yes') : t('common.no')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="media" className="mt-6">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium mb-4">{t('vehicleDetail.media.photos')}</h3>
                  {mediaItems.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {mediaItems.map((item, index) => (
                        <div key={index} className="rounded-lg overflow-hidden border relative group">
                          <img 
                            src={url(item)} 
                            alt={t('vehicleDetail.media.photoAlt', { index: index + 1 })}
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteFile(item, 'fotos', index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">{t('vehicleDetail.media.noPhotos')}</p>
                  )}
                </div>
                
                <div>
                  <h3 className="text-lg font-medium mb-4">{t('vehicleDetail.media.videos')}</h3>
                  {data.midia?.videos?.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {data.midia.videos.map((item, index) => (
                        <div key={index} className="rounded-lg overflow-hidden border relative group">
                          <video 
                            src={url(item)} 
                            controls
                            className="w-full h-auto bg-black"
                          />
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteFile(item, 'videos', index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">{t('vehicleDetail.media.noVideos')}</p>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="attachments" className="mt-6">
              <h3 className="text-lg font-medium mb-4">{t('vehicleDetail.attachments.title')}</h3>
              {attachmentItems.length > 0 ? (
                <div className="space-y-2">
                  {attachmentItems.map((item, index) => {
                    // Compatibilidade: item pode ser string (formato antigo) ou objeto (formato novo)
                    const isNewFormat = typeof item === 'object' && item.arquivo;
                    const fileUrl = isNewFormat ? item.arquivo : item;
                    const fileName = isNewFormat ? item.nome : t('vehicleDetail.attachments.documentNumber', { index: index + 1 });
                    const fileType = isNewFormat ? item.tipo : t('vehicleDetail.attachments.document');
                    
                    return (
                      <div key={index} className="p-4 rounded-lg border flex justify-between items-center">
                        <div>
                          <p className="font-medium">{fileName}</p>
                          <p className="text-sm text-muted-foreground">{fileType}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              // Criar link temporário para download
                              const link = document.createElement('a');
                              link.href = getImageUrl(fileUrl);
                              link.download = fileName;
                              link.target = '_blank';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {t('vehicleDetail.attachments.download')}
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => handleDeleteFile(fileUrl, 'anexos', index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground">{t('vehicleDetail.attachments.noDocuments')}</p>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </Card>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('vehicleDetail.confirmDelete.title')}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t('vehicleDetail.confirmDelete.message', { placa: data?.placa })}
            </p>
            {data?.infoTecnica?.marca && data?.infoTecnica?.modelo && (
              <p className="text-sm text-muted-foreground mt-1">
                {data.infoTecnica.marca} {data.infoTecnica.modelo}
              </p>
            )}
            <p className="text-sm text-muted-foreground mt-2 font-medium text-red-600">
              {t('vehicleDetail.confirmDelete.warning')}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleDeleteCancel} disabled={deleting}>
              {t('vehicleDetail.confirmDelete.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm} 
              disabled={deleting}
            >
              {deleting ? t('vehicleDetail.confirmDelete.deleting') : t('vehicleDetail.confirmDelete.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
